<script setup>
import { ref } from "vue";
import AgeRangeSlider from "./AgeRangeSlider.vue";
import DateGranularityFilter from "./DateGranularityFilter.vue";
import CountryMultiselect from "./CountryMultiselect.vue";
import {
  PURCHASE_TYPE_OPTIONS,
  PLATFORM_OPTIONS,
  STATUS_OPTIONS,
  GENDER_OPTIONS,
  AGE_GROUP_OPTIONS,
  SIGN_IN_BY_OPTIONS,
  VERIFICATION_STATUS_OPTIONS,
  AGE_MIN,
  AGE_MAX,
  planLabel,
} from "../analysisOptions";
import { toggleInList } from "../analysisFormat";

defineProps({
  plans: { type: Array, required: true },
  isWalletTab: { type: Boolean, required: true },
  isPurchasesTab: { type: Boolean, required: true },
});

// `filters` is the parent's `planFilters` reactive object, modeled (rather
// than a plain prop) since this component mutates its fields in place —
// same object identity as before this was split out, not a copy.
const filters = defineModel("filters", { type: Object, required: true });
const purchaseType = defineModel("purchaseType", { type: String, required: true });
const includeSpent = defineModel("includeSpent", { type: Boolean, required: true });
const breakdownMetric = defineModel("breakdownMetric", { type: String, required: true });

const showMoreFilters = ref(false);
</script>

<template>
  <div class="filters-bar">
    <div v-if="isPurchasesTab" class="filter-toggle-group">
      <label class="form-label text-sm fw-semibold d-block">Type</label>
      <div class="d-flex gap-2">
        <button v-for="opt in PURCHASE_TYPE_OPTIONS" :key="opt.value" type="button" class="btn btn-sm mb-0"
          :class="purchaseType === opt.value ? 'btn-success' : 'btn-outline-secondary'"
          @click="purchaseType = opt.value">
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div v-if="isWalletTab" class="filter-toggle-group">
      <label class="form-label text-sm fw-semibold d-block">Include spent</label>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm mb-0"
          :class="includeSpent ? 'btn-outline-secondary' : 'btn-success'" @click="includeSpent = false">
          No
        </button>
        <button type="button" class="btn btn-sm mb-0"
          :class="includeSpent ? 'btn-success' : 'btn-outline-secondary'" @click="includeSpent = true">
          Yes
        </button>
      </div>
    </div>

    <div class="filter-toggle-group">
      <label class="form-label text-sm fw-semibold d-block">Metric</label>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm mb-0"
          :class="breakdownMetric === 'count' ? 'btn-success' : 'btn-outline-secondary'"
          @click="breakdownMetric = 'count'">
          Count
        </button>
        <button type="button" class="btn btn-sm mb-0"
          :class="breakdownMetric === 'amount' ? 'btn-success' : 'btn-outline-secondary'"
          @click="breakdownMetric = 'amount'">
          Sum amount
        </button>
      </div>
    </div>

    <DateGranularityFilter v-model:date-from="filters.dateFrom" v-model:date-to="filters.dateTo"
      v-model:granularity="filters.granularity" v-model:timezone="filters.timezone" />

    <button type="button" class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 mb-0"
      @click="showMoreFilters = !showMoreFilters">
      <span>{{ showMoreFilters ? "Hide filters" : "More filters" }}</span>
      <i class="ni" :class="showMoreFilters ? 'ni-bold-up' : 'ni-bold-down'"></i>
    </button>

    <template v-if="showMoreFilters">
      <CountryMultiselect v-model="filters.addressCountries" label="User country code" />
      <CountryMultiselect v-if="!isWalletTab" v-model="filters.userPlanCountries" label="Purchase country code" />
      <div class="filter-block filter-block-sm">
        <label class="form-label text-sm fw-semibold mb-1">User platform</label>
        <select class="form-select" v-model="filters.platforms" multiple>
          <option v-for="opt in PLATFORM_OPTIONS" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>
      <div class="filter-block">
        <label class="form-label text-sm fw-semibold d-flex justify-content-between mb-1">
          <span>User age range</span>
          <span class="text-secondary">{{ filters.minAge }} - {{ filters.maxAge }}</span>
        </label>
        <AgeRangeSlider v-model:min-age="filters.minAge" v-model:max-age="filters.maxAge" :min="AGE_MIN"
          :max="AGE_MAX" />
      </div>

      <div class="filter-toggle-group">
        <label class="form-label text-sm fw-semibold d-block">User status</label>
        <div class="d-flex flex-wrap gap-2">
          <button v-for="status in STATUS_OPTIONS" :key="status" type="button" class="btn btn-sm mb-0"
            :class="filters.statuses.includes(status) ? 'btn-success' : 'btn-outline-secondary'"
            @click="toggleInList(filters.statuses, status)">
            {{ status }}
          </button>
        </div>
      </div>
      <div class="filter-toggle-group">
        <label class="form-label text-sm fw-semibold d-block">User gender</label>
        <div class="d-flex flex-wrap gap-2">
          <button v-for="opt in GENDER_OPTIONS" :key="opt.value" type="button" class="btn btn-sm mb-0"
            :class="filters.genders.includes(opt.value) ? 'btn-success' : 'btn-outline-secondary'"
            @click="toggleInList(filters.genders, opt.value)">
            {{ opt.label }}
          </button>
        </div>
      </div>
      <div class="filter-toggle-group">
        <label class="form-label text-sm fw-semibold d-block">User age group</label>
        <div class="d-flex flex-wrap gap-2">
          <button v-for="group in AGE_GROUP_OPTIONS" :key="group" type="button" class="btn btn-sm mb-0"
            :class="filters.ageGroups.includes(group) ? 'btn-success' : 'btn-outline-secondary'"
            @click="toggleInList(filters.ageGroups, group)">
            {{ group }}
          </button>
        </div>
      </div>
      <div class="filter-toggle-group">
        <label class="form-label text-sm fw-semibold d-block">User sign-in method</label>
        <div class="d-flex flex-wrap gap-2">
          <button v-for="opt in SIGN_IN_BY_OPTIONS" :key="opt.value" type="button" class="btn btn-sm mb-0"
            :class="filters.signInBys.includes(opt.value) ? 'btn-success' : 'btn-outline-secondary'"
            @click="toggleInList(filters.signInBys, opt.value)">
            {{ opt.label }}
          </button>
        </div>
      </div>
      <div class="filter-toggle-group">
        <label class="form-label text-sm fw-semibold d-block">Plan name</label>
        <div class="d-flex flex-wrap gap-2">
          <button v-for="plan in plans" :key="plan.id" type="button" class="btn btn-sm mb-0"
            :class="filters.planIds.includes(plan.id) ? 'btn-success' : 'btn-outline-secondary'"
            @click="toggleInList(filters.planIds, plan.id)">
            {{ planLabel(plan) }}
          </button>
          <span v-if="plans.length === 0" class="text-xs text-secondary">No plans loaded.</span>
        </div>
      </div>
      <div class="filter-toggle-group">
        <label class="form-label text-sm fw-semibold d-block">User verification status</label>
        <div class="d-flex flex-wrap gap-2">
          <button v-for="opt in VERIFICATION_STATUS_OPTIONS" :key="opt.value" type="button" class="btn btn-sm mb-0"
            :class="filters.verificationStatuses.includes(opt.value) ? 'btn-success' : 'btn-outline-secondary'"
            @click="toggleInList(filters.verificationStatuses, opt.value)">
            {{ opt.label }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
